import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";

import { auth, db } from "../firebase";
import { initializeUserPerks } from "../services/userPerksService";
import { DEMO_FAN_PREVIEW_UID } from "./homeSeedPreview";

export const DEV_TEST_EMAIL = "viktor@100viewsonly.com";

const DEV_TEST_PASSWORD = process.env.REACT_APP_DEV_ACCOUNT_PASSWORD;

function isPrivateNetworkHost(host) {
  return (
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

export function isLocalDev() {
  if (process.env.NODE_ENV === "production") return false;
  if (typeof window === "undefined") return false;

  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    isPrivateNetworkHost(host)
  );
}

async function mergeDemoFanPreviewFields(devProfile) {
  try {
    const demoSnap = await getDoc(doc(db, "users", DEMO_FAN_PREVIEW_UID));
    if (!demoSnap.exists()) return devProfile;

    const demo = demoSnap.data();
    if (!Array.isArray(demo.following) || demo.following.length === 0) {
      return devProfile;
    }

    return {
      ...devProfile,
      following: demo.following,
      subscriptions: demo.subscriptions || [],
      purchasedDrops: demo.purchasedDrops || [],
    };
  } catch (err) {
    console.warn("Demo fan preview fields not copied:", err);
    return devProfile;
  }
}

async function ensureDevTestProfile(user) {
  const profileRef = doc(db, "users", user.uid);
  const snap = await getDoc(profileRef);

  let devProfile = {
    uid: user.uid,
    email: DEV_TEST_EMAIL,
    username: "viktor",
    displayName: "Viktor",
    role: "superadmin",
    isCreator: true,
    verified: true,
    bio: "Developer test account",
    avatar: "",
    bannerImage: "",
    followerCount: 0,
    supporterCount: 0,
    updatedAt: serverTimestamp(),
  };

  devProfile = await mergeDemoFanPreviewFields(devProfile);

  if (!snap.exists()) {
    await setDoc(profileRef, { ...devProfile, createdAt: serverTimestamp() });
  } else {
    await setDoc(profileRef, devProfile, { merge: true });
  }

  try {
    await initializeUserPerks(user.uid);
    await updateDoc(doc(db, "userPerks", user.uid), {
      onboardingComplete: true,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("Dev perks init skipped:", err);
  }
}

async function createDevAccount() {
  const credential = await createUserWithEmailAndPassword(
    auth,
    DEV_TEST_EMAIL,
    DEV_TEST_PASSWORD
  );
  await updateProfile(credential.user, { displayName: "Viktor" });
  return credential;
}

function shouldAttemptDevAccountCreate(err) {
  return (
    err?.code === "auth/user-not-found" ||
    err?.code === "auth/invalid-credential" ||
    err?.code === "auth/invalid-login-credentials" ||
    err?.code === "auth/wrong-password"
  );
}

export async function devLogin() {
  if (!isLocalDev()) {
    throw new Error("Dev login is only available on localhost.");
  }

  if (!DEV_TEST_PASSWORD) {
    throw new Error(
      "Set REACT_APP_DEV_ACCOUNT_PASSWORD in .env.local to use dev login."
    );
  }

  let credential;

  try {
    credential = await signInWithEmailAndPassword(
      auth,
      DEV_TEST_EMAIL,
      DEV_TEST_PASSWORD
    );
  } catch (err) {
    if (!shouldAttemptDevAccountCreate(err)) {
      throw err;
    }

    try {
      credential = await createDevAccount();
    } catch (createErr) {
      if (createErr.code === "auth/email-already-in-use") {
        const mismatch = new Error(
          "Dev account exists with a different password. Set REACT_APP_DEV_ACCOUNT_PASSWORD in .env.local or reset the Firebase Auth user."
        );
        mismatch.code = "auth/dev-password-mismatch";
        throw mismatch;
      }
      throw createErr;
    }
  }

  await ensureDevTestProfile(credential.user);
  return credential;
}
